/**
 * Profile Resolver — typed wrappers for the DB-side profile resolution RPCs.
 *
 * THE CONTRACT (profile localization enterprise pass, 2026-06-07):
 *
 *   display_eligible  →  WHAT TO RENDER (content presence check)
 *   no_index          →  WHAT TO TELL SEARCH ENGINES (robots meta only)
 *   needs_review      →  optional "translation updating" badge — does NOT block display
 *
 * If FE code checks `no_index` to decide whether to render, that's a bug.
 *
 * These RPCs are SECURITY DEFINER and centralize locale resolution + cascade
 * server-side: `locale_served` may differ from `requested_locale` when the
 * requested locale's content is pending (cascade serves the best available
 * version — typically EN). The FE never re-implements that decision.
 *
 * - resolve_profile_by_vanity_url(p_vanity, p_locale) — single profile page
 * - resolve_profiles_for_listing(p_user_ids, p_locale) — bulk cards (≤200/call,
 *   results ordered to match input ids)
 *
 * NOTE: these functions are not yet in the generated supabase types
 * (types.ts regeneration tracked separately), hence the local contract types
 * and the narrow rpc cast below.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

// ============================================================================
// CONTRACT TYPES (mirror the RPC payloads — verified against production)
// ============================================================================

export interface ResolvedProfileUser {
  id: string;
  vanity_url: string;
  display_name: string;
  name: string | null;
  lastname: string | null;
  business: string | null;
  image: string | null;
  marker_image: string | null;
  website: string | null;
  linkedin: string | null;
  calendar: string | null;
  location_id: number | null;
  state_province: string | null;
  city: string | null;
  coordinates: unknown;
  type: string | null;
  firm_type: string | null;
  membership_tier: string | null;
  profile_verified: boolean;
  about_source_language: string;
  /** Safety fallback body — render this when display_eligible=false */
  about_en: string;
  /** Present on the single-profile RPC only */
  profile_map_image?: string | null;
}

export interface ResolvedProfileLocalized {
  /** May differ from requested_locale when the cascade served a fallback */
  locale_served: string;
  requested_locale: string;
  /** Bio — from cascade if the requested locale is pending */
  translated_body: string | null;
  translated_name: null; // profiles don't translate names
  translated_slug: null; // profiles don't translate slugs
  meta_title: null;      // profiles don't have meta_title (display_name is the title)
  meta_description: string | null;
  aio_snippet: string | null;
  schema_about: string | null;
  /** Robots meta ONLY — never a render gate */
  no_index: boolean;
  /** Render gate — content presence check */
  display_eligible: boolean;
  needs_review: boolean;
  is_authoritative: boolean;
  updated_at: string;
  /** Only set on synthetic fallback */
  fallback_source?: string;
}

export interface ResolvedProfile {
  found: boolean;
  reason?: string; // only if !found
  canonical_path: string; // always "/{vanity_url}"
  user: ResolvedProfileUser;
  localized: ResolvedProfileLocalized;
}

export interface ListingProfile {
  user: ResolvedProfileUser;
  localized: ResolvedProfileLocalized;
  canonical_path: string;
}

interface ListingResult {
  count: number;
  requested_locale: string;
  profiles: ListingProfile[];
}

/**
 * The body a component should render for a profile, per the contract:
 * display_eligible decides between localized body and the EN safety fallback.
 */
export function resolveDisplayBody(p: { user: Pick<ResolvedProfileUser, 'about_en'>; localized: Pick<ResolvedProfileLocalized, 'display_eligible' | 'translated_body'> }): string | null {
  return p.localized.display_eligible
    ? (p.localized.translated_body ?? p.user.about_en ?? null)
    : (p.user.about_en ?? null);
}

// ============================================================================
// RPC CALLERS
// ============================================================================

/** Listing RPC hard limit per call (enforced DB-side) */
const LISTING_CHUNK_SIZE = 200;

// The resolver RPCs are not in the generated types yet — narrow local cast.
type RpcCaller = (
  fn: string,
  args?: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const rpc = supabase.rpc.bind(supabase) as unknown as RpcCaller;

/**
 * Resolve a single profile by vanity URL for the given locale.
 * Returns null on transport error; { found: false } when the vanity doesn't exist.
 */
export async function fetchResolvedProfileByVanity(
  vanity: string,
  locale: string,
): Promise<ResolvedProfile | null> {
  const { data, error } = await rpc('resolve_profile_by_vanity_url', {
    p_vanity: vanity.toLowerCase(),
    p_locale: locale,
  });

  if (error) {
    logger.error('resolve_profile_by_vanity_url failed', { vanity, locale, error: error.message });
    return null;
  }
  return (data as ResolvedProfile) ?? null;
}

/**
 * Resolve profiles for listing/cards in bulk. Chunks beyond the RPC's 200-id
 * limit transparently; output order matches the input ids (the RPC preserves
 * order per chunk, and chunks are concatenated in order).
 */
export async function fetchResolvedProfilesForListing(
  userIds: string[],
  locale: string,
): Promise<ListingProfile[]> {
  if (userIds.length === 0) return [];

  const chunks: string[][] = [];
  for (let i = 0; i < userIds.length; i += LISTING_CHUNK_SIZE) {
    chunks.push(userIds.slice(i, i + LISTING_CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const { data, error } = await rpc('resolve_profiles_for_listing', {
        p_user_ids: chunk,
        p_locale: locale,
      });
      if (error) {
        logger.error('resolve_profiles_for_listing failed', {
          count: chunk.length,
          locale,
          error: error.message,
        });
        return [] as ListingProfile[];
      }
      return ((data as ListingResult | null)?.profiles ?? []) as ListingProfile[];
    }),
  );

  return results.flat();
}
